/** Dashboard layout for admin, designer, and portal pages. */

import {
	SidebarInset,
	SidebarProvider,
	SidebarTrigger,
} from "@designs-by-maryam/ui/components/sidebar";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { toast } from "sonner";
import { AppSidebar } from "@/components/dashboard/app-sidebar";
import { getUser } from "@/functions/get-user";
import { useSessionTimeout } from "@/hooks/use-session-timeout";

// Lazy load command menu for better initial bundle
const CommandMenu = lazy(() =>
	import("@/components/dashboard/command-menu").then((m) => ({
		default: m.CommandMenu,
	})),
);

export const Route = createFileRoute("/_dashboard")({
	component: DashboardLayout,
	beforeLoad: async () => {
		try {
			const session = await getUser();
			if (!session?.data) {
				throw redirect({ to: "/login" });
			}
			return { session };
		} catch (error) {
			// If it's already a redirect, rethrow it
			if (
				error instanceof Response ||
				(error && typeof error === "object" && "to" in error)
			) {
				throw error;
			}
			// For any other error (network, auth service down), redirect to login
			// Note: Error details are captured by Sentry for debugging
			throw redirect({ to: "/login" });
		}
	},
});

function DashboardLayout() {
	useSessionTimeout({
		onSessionExpiring: (expiresAt) => {
			const minutesLeft = Math.round(
				(expiresAt.getTime() - Date.now()) / 60000,
			);
			toast.warning("Session Expiring", {
				description: `Your session will expire in ${minutesLeft} minute${minutesLeft === 1 ? "" : "s"}. Please save your work.`,
				duration: 10000,
			});
		},
		onSessionExpired: () => {
			toast.error("Session Expired", {
				description: "You have been logged out due to inactivity.",
			});
		},
		autoRedirect: true,
	});

	return (
		<SidebarProvider defaultOpen>
			<AppSidebar />
			<SidebarInset>
				{/* Dashboard Header */}
				<header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
					<SidebarTrigger className="-ml-1" />
					<div className="flex-1" />
					<kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-medium font-mono text-muted-foreground text-xs sm:flex">
						<span className="text-xs">⌘</span>K
					</kbd>
				</header>

				{/* Dashboard Content */}
				<div className="flex-1 overflow-auto p-4 md:p-6">
					<Outlet />
				</div>
			</SidebarInset>

			{/* Command Menu (Cmd+K) */}
			<Suspense fallback={null}>
				<CommandMenu />
			</Suspense>
		</SidebarProvider>
	);
}
